import { Typography } from '@euler/app/components/typography';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCouponCampaignSummary } from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { Check, ChevronLeft } from 'lucide-react-native';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, View } from 'react-native';
import { couponItemText, couponPeriodText } from '../components/couponText';
import { aiColors, tint } from '../theme';

type ListState = {
  coupons: ApdmCouponCampaignSummary[];
  /** Where the next page starts; null once the last page is in. */
  next: string | null;
  loading: boolean;
  failed: boolean;
};

const kFirstPage: ListState = {
  coupons: [],
  next: null,
  loading: true,
  failed: false,
};

/**
 * Every coupon campaign the plan may carry, newest first, read a page at a
 * time; a tap picks one for the card and closes the sheet.
 */
export const PlanCouponChoicesSheet = memo(
  ({
    messageId,
    visible,
    selected,
    onPick,
    onClose,
  }: {
    messageId: string;
    visible: boolean;
    /** The one the card has selected. */
    selected: string | null;
    onPick: (coupon: ApdmCouponCampaignSummary) => void;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { apdmCampaignService } = useServiceFactory();
    const [state, setState] = useState<ListState>(kFirstPage);
    // One read at a time: the list's end fires again while a page loads.
    const reading = useRef(false);
    // A page that lands after the sheet closed or reopened is dropped.
    const generation = useRef(0);
    const read = useCallback(
      async (after: string | null) => {
        if (reading.current) return;
        const mine = generation.current;
        reading.current = true;
        setState(current => ({ ...current, loading: true, failed: false }));
        try {
          const page = await apdmCampaignService.planCouponChoices({
            messageId,
            afterCouponCampaignId: after,
          });
          if (mine !== generation.current) return;
          setState(current => ({
            coupons: after
              ? [...current.coupons, ...page.choices]
              : page.choices,
            next: page.nextAfterCouponCampaignId,
            loading: false,
            failed: false,
          }));
        } catch (error) {
          console.error('APDM plan coupon list failed', error);
          if (mine === generation.current)
            setState(current => ({ ...current, loading: false, failed: true }));
        } finally {
          if (mine === generation.current) reading.current = false;
        }
      },
      [apdmCampaignService, messageId],
    );
    useEffect(() => {
      generation.current++;
      reading.current = false;
      if (!visible) return;
      setState(kFirstPage);
      void read(null);
    }, [visible, read]);
    return (
      // As tall as its coupons, up to the panel's cap; then the list
      // scrolls. A content-sized panel needs content-sized wrappers: `flex:
      // 1` there has no height to fill and collapses on a device.
      <BottomPanel visible={visible} onClose={onClose} fitContent>
        <View style={{ flexShrink: 1, backgroundColor: aiColors.background }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(10),
              paddingHorizontal: rpx(12),
              paddingVertical: rpx(12),
              backgroundColor: aiColors.card,
              borderBottomWidth: 1,
              borderBottomColor: aiColors.border,
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="关闭"
              onPress={onClose}
              hitSlop={8}
            >
              <ChevronLeft size={22} color={aiColors.foreground} />
            </Pressable>
            <Typography.Text
              bold
              size={rpx(15)}
              color={aiColors.foreground}
              style={{ flex: 1 }}
            >
              全部优惠券活动
            </Typography.Text>
          </View>
          <FlatList
            style={{ flexGrow: 0, flexShrink: 1 }}
            data={state.coupons}
            keyExtractor={coupon => coupon.id}
            renderItem={({ item }) => (
              <CouponRow
                coupon={item}
                selected={item.id === selected}
                onPress={() => onPick(item)}
              />
            )}
            contentContainerStyle={{ padding: rpx(16), gap: rpx(10) }}
            onEndReachedThreshold={0.5}
            onEndReached={() => {
              if (state.next && !state.loading && !state.failed)
                void read(state.next);
            }}
            ListFooterComponent={
              state.loading ? (
                <ActivityIndicator
                  color={aiColors.primary}
                  style={{ paddingVertical: rpx(20) }}
                />
              ) : state.failed ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void read(state.next)}
                  style={{ paddingVertical: rpx(20), alignItems: 'center' }}
                >
                  <Typography.Text size={rpx(13)} color={aiColors.primary}>
                    没有读取到，点此重试
                  </Typography.Text>
                </Pressable>
              ) : null
            }
          />
        </View>
      </BottomPanel>
    );
  },
);

const CouponRow = memo(
  ({
    coupon,
    selected,
    onPress,
  }: {
    coupon: ApdmCouponCampaignSummary;
    selected: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Pressable
        accessibilityRole="radio"
        accessibilityState={{ checked: selected }}
        onPress={onPress}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(10),
          padding: rpx(12),
          borderRadius: rpx(12),
          borderWidth: selected ? 2 : 1,
          borderColor: selected ? aiColors.primary : aiColors.border,
          backgroundColor: selected
            ? tint(aiColors.primary, 0.04)
            : aiColors.card,
          opacity: pressed ? 0.7 : 1,
        })}
      >
        <View style={{ flex: 1, gap: rpx(4) }}>
          <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
            {coupon.title}
          </Typography.Text>
          {coupon.items.slice(0, 3).map((item, index) => (
            <Typography.Text
              key={index}
              size={rpx(12)}
              color={aiColors.foreground}
              numberOfLines={1}
            >
              · {couponItemText(item)}
            </Typography.Text>
          ))}
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {couponPeriodText(coupon)}
          </Typography.Text>
        </View>
        {selected ? <Check size={rpx(18)} color={aiColors.primary} /> : null}
      </Pressable>
    );
  },
);
